import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminShell } from '@/apps/coach/components/admin/AdminShell';
import { routes } from '@/lib/routes';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';

type Analytics = Awaited<ReturnType<typeof api.operator.analytics>>;

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toIsoDate(d);
}

const PRESETS: Array<{ label: string; days: number }> = [
  { label: '7 Tage', days: 7 },
  { label: '30 Tage', days: 30 },
  { label: '90 Tage', days: 90 },
  { label: '1 Jahr', days: 365 },
];

export function Analytics() {
  const navigate = useNavigate();
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(toIsoDate(new Date()));
  const [data, setData] = useState<Analytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.operator.analytics({ from, to })
      .then(setData)
      .catch((err) => {
        const msg = err instanceof Error ? err.message : '';
        if (msg.startsWith('API 401')) {
          navigate(routes.coach.welcome(), { replace: true });
          return;
        }
        setError(msg || 'Fehler beim Laden.');
      })
      .finally(() => setLoading(false));
  }, [from, to, navigate]);

  function applyPreset(days: number) {
    setFrom(daysAgo(days - 1));
    setTo(toIsoDate(new Date()));
  }

  const activePresetDays = (() => {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const days = Math.round((toDate.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000)) + 1;
    const todayIso = toIsoDate(new Date());
    if (to !== todayIso) return null;
    const preset = PRESETS.find((p) => p.days === days);
    return preset ? preset.days : null;
  })();

  const maxDaily = data ? Math.max(1, ...data.daily.map((d) => d.messages)) : 1;

  return (
    <AdminShell pageTitle="Analytics">
      <PageHeader title="Analytics" showBack />
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6">

        {/* Range selector */}
        <section className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <Button
                key={p.days}
                size="sm"
                variant={activePresetDays === p.days ? 'default' : 'outline'}
                onClick={() => applyPreset(p.days)}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Von
              <Input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                max={to}
                className="w-auto text-xs"
              />
            </label>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Bis
              <Input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                min={from}
                max={toIsoDate(new Date())}
                className="w-auto text-xs"
              />
            </label>
          </div>
        </section>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {!loading && data && (
          <>
            {/* Daily activity */}
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground">Tagesaktivität</h2>
              {data.daily.length === 0 ? (
                <p className="text-sm text-muted-foreground">Keine Daten im gewählten Zeitraum.</p>
              ) : (
                <div className="rounded-xl border border-border divide-y divide-border">
                  {data.daily.slice().reverse().map((d) => {
                    const pct = (d.messages / maxDaily) * 100;
                    return (
                      <div key={d.date} className="flex items-center gap-3 px-3 py-2 text-xs">
                        <span className="w-12 shrink-0 text-muted-foreground tabular-nums">{formatDate(d.date)}</span>
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          {d.messages > 0 && (
                            <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
                          )}
                        </div>
                        <span className="w-8 shrink-0 text-right tabular-nums">{d.messages}</span>
                        <span className="w-12 shrink-0 text-right text-muted-foreground tabular-nums">
                          {d.activeUsers} User
                        </span>
                      </div>
                    );
                  })}
                  <div className="flex items-center gap-3 px-3 py-2 text-xs font-semibold bg-muted/40">
                    <span className="w-12 shrink-0 text-muted-foreground">Summe</span>
                    <div className="flex-1" />
                    <span className="w-8 shrink-0 text-right tabular-nums">{data.summary.totalMessages}</span>
                    <span className="w-12 shrink-0 text-right text-muted-foreground tabular-nums">
                      {data.summary.uniqueUsers} User
                    </span>
                  </div>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Balken = Anzahl Nachrichten. Rechts: aktive Nutzer an dem Tag. Summe zählt eindeutige Nutzer im ganzen Zeitraum.
              </p>
            </section>

            {/* Top users */}
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground">Top-Nutzer nach Verbrauch</h2>
              {data.topUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground">Noch kein Verbrauch.</p>
              ) : (
                <div className="space-y-2">
                  {data.topUsers.map((u) => (
                    <Link
                      key={u.userId}
                      to={routes.coach.operator.user(u.userId)}
                      className="block rounded-xl border border-border px-4 py-3 hover:bg-muted transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{u.name || u.email}</p>
                          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                        </div>
                        <div className="shrink-0 text-xs text-right">
                          <p className="font-mono text-sm font-semibold">
                            {u.usedCount} / {u.usageLimit}
                          </p>
                          <p className="text-muted-foreground">{u.voucherCode}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">Aktueller Stand, nicht vom Zeitraum abhängig.</p>
            </section>

            {/* Voucher utilization */}
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-muted-foreground">Voucher-Auslastung</h2>
              {data.voucherUtilization.length === 0 ? (
                <p className="text-sm text-muted-foreground">Keine aktiven Gutscheine.</p>
              ) : (
                <div className="space-y-2">
                  {data.voucherUtilization.map((v) => (
                    <Link
                      key={v.code}
                      to={routes.coach.operator.voucher(v.code)}
                      className="block rounded-xl border border-border px-4 py-3 space-y-2 hover:bg-muted transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-mono text-sm font-semibold">{v.code}</p>
                          <p className="text-xs text-muted-foreground">
                            {v.scope === 'global' ? 'Gemeinsames Budget' : 'Pro-Nutzer-Budget'} ·
                            {' '}{v.redemptionCount} Einlösung(en)
                          </p>
                        </div>
                        <span className="text-sm font-mono font-semibold tabular-nums">
                          {v.utilizationPercent}%
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${Math.min(100, v.utilizationPercent)}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {v.scope === 'global'
                          ? `${v.totalUsed} von ${v.usageLimit} Nachrichten verbraucht`
                          : `${v.totalUsed} Nachrichten insgesamt · Ø ${v.redemptionCount > 0 ? Math.round(v.totalUsed / v.redemptionCount) : 0} pro Einlöser (max. ${v.usageLimit})`}
                      </p>
                    </Link>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">Aktueller Stand, nicht vom Zeitraum abhängig.</p>
            </section>
          </>
        )}

      </div>
    </AdminShell>
  );
}
